import api from "@/services/api";
import type { IAddPackagingSectionPayload } from "@/types/Packaging.types";
import type {
  GetPackagingTabSectionsResponse,
  GetPackagingTabSitesOnloadResponse,
  PackagingTabSavePayload,
  PackagingTabSaveResponse,
} from "@/components/ActiveProjects/PackagingTab/types/packagingTabTypes";

const { productServiceAPI, pimcoreServiceAPI, glossaryServiceAPI } = api;

// Product service (listing + detail + save)
const PACKAGING_TAB_SECTION_LIST_PATH =
  "/product/specification/activeProjects/sections/packagingListing";
const PACKAGING_TAB_SECTION_DETAIL_PATH = "/product/packaging-tab";

// Pimcore (create/delete)
const PACKAGING_TAB_CREATE_SECTION_PATH = "/shared/packagingTab/createPackagingTab";
const PACKAGING_TAB_DELETE_SECTION_PATH = "/shared/packagingTab/deletePackagingTab";

// Glossary service (sites dropdown)
const PACKAGING_TAB_SITES_ONLOAD_PATH = "/glossary/sites-onload";

const PackagingTabService = {
  /**
   * New Packaging tab: section list (by vendorAssessmentId).
   * GET /product/specification/activeProjects/sections/packagingListing/{vendorAssessmentId}
   */
  getPackagingTabSections: async (vendorAssessmentId?: string | number): Promise<any> => {
    const url = `${PACKAGING_TAB_SECTION_LIST_PATH}/${encodeURIComponent(String(vendorAssessmentId))}`;
    const resp = await productServiceAPI.get(url);
    return resp.data;
  },

  /**
   * New Packaging tab: section detail (by sectionId).
   * GET /product/packaging-tab/{sectionId}
   */
  getPackagingTabSectionById: async (
    sectionId?: string | number
  ): Promise<GetPackagingTabSectionsResponse> => {
    const url = `${PACKAGING_TAB_SECTION_DETAIL_PATH}/${encodeURIComponent(String(sectionId))}`;
    const resp = await productServiceAPI.get(url);
    return resp.data;
  },

  /**
   * New Packaging tab: save section data.
   * PUT /product/packaging-tab/{sectionId}
   */
  savePackagingTabSectionById: async (
    sectionId: string | number,
    payload: PackagingTabSavePayload
  ): Promise<PackagingTabSaveResponse> => {
    const url = `${PACKAGING_TAB_SECTION_DETAIL_PATH}/${encodeURIComponent(String(sectionId))}`;
    const resp = await productServiceAPI.put(url, payload);
    return resp.data;
  },

  /**
   * New Packaging tab: create section.
   * POST /shared/packagingTab/createPackagingTab
   * Payload: { sectionName: "Untitled", vendorAssessment: { id } }
   * Response: { id: "123123" }
   */
  createPackagingTabSection: async (
    payload: IAddPackagingSectionPayload
  ): Promise<{ id: string | number }> => {
    const resp = await pimcoreServiceAPI.post(PACKAGING_TAB_CREATE_SECTION_PATH, payload);
    return resp.data;
  },

  /** New Packaging tab: delete section. POST /shared/packagingTab/deletePackagingTab { id } */
  deletePackagingTabSection: async (sectionId: string | number): Promise<any> => {
    const payload = { id: Number(sectionId) };
    const resp = await pimcoreServiceAPI.post(PACKAGING_TAB_DELETE_SECTION_PATH, payload);
    return resp.data;
  },

  /**
   * New Packaging tab: sites dropdown onload.
   * GET /glossary/sites-onload?vendorAssessmentId=...
   */
  getPackagingTabSitesOnload: async (
    vendorAssessmentId: string | number
  ): Promise<GetPackagingTabSitesOnloadResponse> => {
    const resp = await glossaryServiceAPI.get(PACKAGING_TAB_SITES_ONLOAD_PATH, {
      params: { vendorAssessmentId: String(vendorAssessmentId) },
    });
    return resp.data;
  },
};

export default PackagingTabService;
